import {
  Component,
  Input,
  Output,
  EventEmitter,
  CUSTOM_ELEMENTS_SCHEMA,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import type { LeafSelect } from '@cigna/leaf-web/lib/wc';
import '@cigna/leaf-web/lib/wc/components/select/select';

@Component({
  selector: 'cigna-leaf-select',
  template: `
    <leaf-select
      [class.fullWidth]="fullWidth"
      [value]="value"
      [label]="label"
      [items]="items"
      [name]="name"
      [fieldId]="fieldId"
      [fieldNote]="fieldNote"
      [ariaLive]="ariaLive"
      [ariaDescribedBy]="ariaDescribedBy"
      [required]="required"
      [isError]="isError"
      [errorNote]="isError ? errorNote : undefined"
      [validationAriaDescribedBy]="validationAriaDescribedBy"
      [isSuccess]="isSuccess"
      [successNote]="isSuccess ? successNote : undefined"
      [isOptional]="isOptional"
      [optionalText]="optionalText"
      [disabled]="disabled"
      (blur)="onTouched()"
      (change)="changeHandler($event)"
    ></leaf-select>
  `,
  styles: `.fullWidth { width: 100%; display:block; }`,
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CignaLeafSelectComponent),
      multi: true,
    },
  ],
})
export class CignaLeafSelectComponent implements ControlValueAccessor {
  @Input({ required: true }) label: LeafSelect['label'];
  @Input({ required: true }) items: LeafSelect['items'];

  @Input() ariaDescribedBy: LeafSelect['ariaDescribedBy'];
  @Input() ariaLive: LeafSelect['ariaLive'];
  @Input() errorNote: LeafSelect['errorNote'];
  @Input() fieldId: LeafSelect['fieldId'];
  @Input() fieldNote: LeafSelect['fieldNote'];
  @Input() isError: LeafSelect['isError'];
  @Input() isSuccess: LeafSelect['isSuccess'];
  @Input() name: LeafSelect['name'];
  @Input() required: LeafSelect['required'];
  @Input() successNote: LeafSelect['successNote'];
  @Input() validationAriaDescribedBy: LeafSelect['validationAriaDescribedBy'];
  @Input() isOptional: LeafSelect['isOptional'] = false;
  @Input() optionalText: LeafSelect['optionalText'];
  @Input() disabled: LeafSelect['disabled'];

  /**
   * ********
   * CUSTOM ANGULAR PROPERTIES
   */
  @Input() fullWidth = true;
  @Output() change = new EventEmitter<string | number>();

  private _value = '';
  onChange: any = () => {};
  onTouched: any = () => {};

  changeHandler(event: any) {
    const val = event.target.internalValue;
    this.onChange(val);
    this.change.emit(val);
  }

  @Input()
  get value() {
    return this._value;
  }

  set value(val) {
    if (val !== this._value) {
      this._value = val;
      this.onTouched();
    }
  }

  writeValue(value: string) {
    this.value = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
